import {useRef, useState} from 'react'
import DatePicker from 'react-datepicker'
import Popper from '@mui/material/Popper'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import {format} from 'date-fns'
import 'react-datepicker/dist/react-datepicker.css'
import './DateRangeChip.css'
import {DateRange} from './types'

type Props = {
  label: string
  value: DateRange
  onChange: (value: DateRange) => void
  maxDate?: Date
}

function parseDate(value: string): Date | null {
  return value ? new Date(`${value}T00:00:00`) : null
}

// IMPORTANTE: nunca usar `date.toISOString()` aqui — converte pra UTC e, no
// fuso do projeto (America/Sao_Paulo, UTC-3), desloca a data selecionada em -1
// dia. `date-fns` já é dependência do projeto (usado com esse mesmo padrão em
// outros pontos da app).
function formatDate(date: Date): string {
  return format(date, 'yyyy-MM-dd')
}

function formatLabel(value: DateRange): string {
  if (!value.begin_date && !value.end_date) return 'Qualquer período'
  const fmt = (d: string) => (d ? d.split('-').reverse().join('/') : '…')
  return `${fmt(value.begin_date)} – ${fmt(value.end_date)}`
}

const DateRangeChip = ({label, value, onChange, maxDate}: Props) => {
  const triggerRef = useRef<HTMLDivElement>(null)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const startDate = parseDate(value.begin_date)
  const endDate = parseDate(value.end_date)

  const handleToggle = () => {
    setAnchorEl((prev) => (prev ? null : triggerRef.current))
  }

  const handleClose = () => setAnchorEl(null)

  return (
    <div className='d-flex flex-column' ref={triggerRef}>
      <label className='d-block fs-7 mb-1'>{label}</label>
      <button
        type='button'
        className='btn btn-sm date-range-chip w-100 text-start'
        onClick={handleToggle}
      >
        <i className='bi bi-calendar3 me-2' />
        {formatLabel(value)}
      </button>

      {/* @ts-ignore — mesmo padrão já usado em UsersSymbolGroupColumn.tsx pro Popper do MUI */}
      <Popper open={open} anchorEl={anchorEl} placement='bottom-start' style={{zIndex: 1200}}>
        {open && (
          <ClickAwayListener onClickAway={handleClose}>
            <div className='date-range-chip-popover'>
              <DatePicker
                selectsRange
                inline
                startDate={startDate}
                endDate={endDate}
                maxDate={maxDate}
                onChange={(dates) => {
                  const [start, end] = dates as [Date | null, Date | null]
                  onChange({
                    begin_date: start ? formatDate(start) : '',
                    end_date: end ? formatDate(end) : '',
                  })
                }}
              />
              <div className='d-flex justify-content-between p-2 border-top'>
                <button
                  type='button'
                  className='btn btn-sm btn-light'
                  onClick={() => {
                    onChange({begin_date: '', end_date: ''})
                    handleClose()
                  }}
                >
                  Limpar
                </button>
                <button type='button' className='btn btn-sm btn-primary' onClick={handleClose}>
                  Aplicar
                </button>
              </div>
            </div>
          </ClickAwayListener>
        )}
      </Popper>
    </div>
  )
}

export {DateRangeChip}
